// The blog post registry — the single source of truth for the Blog section.
// routes/blog.tsx builds the index from it; each post route reads its own
// entry; BlogLayout builds prev/next navigation from it.
//
// Lifecycle:
//   planned      — backlog only (kept in comments / the roadmap). No entry.
//   placeholder  — a real, navigable post with a "coming soon" body. Listed
//                  on the index and in prev/next, but noindex and no
//                  BlogPosting JSON-LD so thin pages never reach search.
//   draft        — a finished body that is not live yet. Routable (so a
//                  preview link works) and rendered in <BlogLayout>, but
//                  left out of "keep reading" and prev/next, never in
//                  public/sitemap.xml, and noindex with no BlogPosting
//                  JSON-LD. On pine59.com it is also left out of the index
//                  and category pages; on PR previews and localhost it is
//                  listed there (see DRAFT_POSTS) so the cover can be
//                  reviewed as it will look at launch.
//   published    — finished, indexable, with article metadata.
//
// Publishing a draft: flip `status` to "published", add the URL to
// public/sitemap.xml, and add a "- [Title](url): summary" line to the blog
// list in public/llms.txt (it lists every published post by hand, so LLMs
// can find it).
//
// Promoting a placeholder: replace <PlaceholderPost> in the route with the
// real body, flip `status` to "published", set `published` +
// `publishedDisplay`, and add the URL to public/sitemap.xml.

import type { MetaDescriptor } from "react-router"
import { FOOT_TRAFFIC_CARD_CHART_LINE } from "~/data/card-art/what-is-foot-traffic"

import {
  OG_IMAGE,
  SITE_NAME,
  SITE_URL,
  canonicalUrl,
  pageMeta,
} from "~/lib/meta"

export type PostStatus = "published" | "draft" | "placeholder" | "planned"

// The one axis the index filters on — an editorial format, deliberately not
// overlapping with Docs ("guide", "example", "concept") or Solutions ("use
// case"):
//   news        company / product / partnership announcements
//   basics      plain-language concept explainers, no product needed — the
//               public counterpart to a Docs Concept
//   research    original findings from our own data; rankings, benchmarks
//   comparison  buyer-stage evaluation help — "X vs Y", "how to choose"
export type PostType = "news" | "basics" | "research" | "comparison"

// Tab order on the index. `label` is the (plural) filter label; `blurb` is
// the one-line description under the heading on /blog/category/<value>.
// `tab` marks the types that get their own filter tab on the index — we keep
// the taxonomy small on the surface (All / News / Basics) while research and
// comparison posts still carry their type on the card and their own
// /blog/category/<value> page.
export const POST_TYPES: {
  value: PostType
  label: string
  blurb: string
  tab?: boolean
}[] = [
  {
    value: "news",
    label: "News",
    blurb: "Company news, product updates and partnership announcements.",
    tab: true,
  },
  {
    value: "basics",
    label: "Basics",
    blurb: "Plain-language explainers on the concepts behind location data.",
    tab: true,
  },
  {
    value: "research",
    label: "Research",
    blurb: "Original findings and rankings from Pine59's own measurement.",
  },
  {
    value: "comparison",
    label: "Comparisons",
    blurb: "How to evaluate and choose a location data provider.",
  },
]

// The line under the heading on /blog (the "All" view).
export const BLOG_INTRO = "Explainers and product news from the Pine59 team."

const TYPE_LABEL: Record<PostType, string> = {
  news: "News",
  basics: "Basics",
  research: "Research",
  comparison: "Comparison",
}

// Singular label for a card / the post hero.
export function typeLabel(type: PostType): string {
  return TYPE_LABEL[type]
}

// The POST_TYPES entry for a raw slug — used to validate /blog/category/<slug>.
export function getPostType(value: string) {
  return POST_TYPES.find((t) => t.value === value)
}

// `box` is the plot area in % of the card image; `path` is SVG path data in a
// viewBox matching that box's own pixel proportions (1 path unit = 1 image
// pixel, so no rescaling to work out). The mask gradient hides the baked line
// at rest so it reads as freshly drawn.
export type CardChartLine = {
  box: { top: string; left: string; width: string; height: string }
  viewBox: string
  path: string
  color: string
  maskFrom: string
  maskTo: string
}

export type Post = {
  slug: string
  title: string
  // One-line dek shown on the card and used as the meta description.
  summary: string
  // Standfirst under the title in the post's own hero, when it should read
  // differently from `summary`. Falls back to `summary`.
  dek?: string
  // ISO date. Undefined until published.
  published?: string
  // Human display string, e.g. "September 1, 2026". "Coming soon" for
  // placeholders.
  publishedDisplay?: string
  // Optional last-modified ISO date for article:modified_time / JSON-LD.
  modified?: string
  // Index-card thumbnail (path under /public, keep it under /art). 1600x1000
  // webp, with an 800px-wide sibling at `<name>-800.webp` that <PostThumb>
  // derives for srcSet — keep both when adding art. A directory in public/
  // shadows the route of the same name: public/blog/ would turn /blog into a
  // 301 to /blog/ via the static file server.
  cardImage?: string
  // The og:image / twitter:image shown when this post's link is shared —
  // deliberately its own asset, not just `cardImage` cropped differently.
  // Must be exactly 1200x630 (pageMeta declares that size for every page).
  // See postSocialImage() for the fallback chain.
  socialImage?: string
  // Percentage position (within `cardImage`) of a focal point baked into the
  // art — e.g. a map pin — that gets a soft lime glow on card hover.
  cardImageHotspot?: { top: string; left: string }
  // A shape baked into the art (e.g. a phone) that glows from behind on
  // hover: an outward-only halo around its outline, so the shape itself
  // stays crisp. Percentages of the 16:10 card.
  cardImageHalo?: {
    top: string
    left: string
    width: string
    height: string
    radius: string
  }
  // Several focal points that glow in sequence (left to right) on hover
  // rather than all at once — e.g. a row of map pins along a path.
  cardImageHotspots?: { top: string; left: string }[]
  // A line chart baked into `cardImage` that gets redrawn as a live stroke
  // animation on hover. The traced data lives under data/card-art/.
  cardChartLine?: CardChartLine
  // Optional art behind the post's own hero (see CoverBackdrop in
  // BlogLayout): sits on the right on desktop, dimmed full-bleed on phones.
  // Must be text-free; a cover with a baked-in logo or headline collides
  // with the title. Usually the same file as `cardImage` when that is pure
  // artwork. Posts without it keep the plain forest band.
  heroImage?: string
  // Wide (~5.6:1) banner art — the source cover artwork cardImage is cropped
  // from, image bleeding directly into the dark texture with no baked text.
  // Used by BannerPostCard (the "From the blog" card on /solutions) as a
  // full-bleed background with real title/byline text overlaid on the plain
  // dark side, instead of a separately boxed thumbnail. Not every post has
  // one exported at this aspect yet.
  bannerImage?: string
  // The editorial format — drives the index filter.
  type: PostType
  // Author ids from data/authors.ts. One or more.
  authors?: string[]
  // Ids from data/authors.ts for people quoted/interviewed but not writing —
  // a guest the post owes an introduction to (e.g. a partner-post Q&A
  // subject). Rendered as its own "Interviewed" row in <WrittenBy>.
  interviewees?: string[]
  // Subject areas (Retail, Cities, …). Secondary metadata shown on the card;
  // not a primary filter yet.
  topics?: string[]
  // Pulled out into the large cards at the top of the index. Keep this to a
  // handful; everything else flows into the dense grid below.
  featured?: boolean
  status: PostStatus
  // The section outline a placeholder previews, and a hint to whoever writes
  // it. Not rendered once the post is published.
  outline?: string[]
  // Who is writing it / why it matters. Never rendered.
  note?: string
}

export const POSTS: Post[] = [
  {
    slug: "welcome-to-pine59",
    title: "Welcome to Pine59",
    summary: "A new name. A sharper focus. The same mission.",
    published: "2026-09-01",
    publishedDisplay: "September 1, 2026",
    modified: "2026-09-01",
    cardImage: "/art/blog/welcome-to-pine59-card.webp",
    socialImage: "/art/blog/social-preview-welcome-to-pine59.webp",
    type: "news",
    authors: ["torage", "thomas", "kjartan"],
    status: "published",
  },

  // ── The first posts — placeholders until their owners write them ────────
  {
    slug: "retailnext-partnership",
    title: "Pine59 + RetailNext: connecting the full retail journey",
    summary:
      "In-store performance data meets beyond-the-store market intelligence. Alexei Agratchev and Thomas Walle on why connecting both sides of the door changes what retailers can decide.",
    published: "2026-09-08",
    publishedDisplay: "September 8, 2026",
    modified: "2026-09-08",
    cardImage: "/art/blog/retailnext-partnership-card.webp",
    socialImage: "/art/blog/social-preview-retailnext-partnership.webp",
    bannerImage: "/art/blog/retailnext-partnership-banner.webp",
    heroImage: "/art/blog/retailnext-partnership/hero.webp",
    type: "news",
    authors: ["thomas"],
    interviewees: ["alexei"],
    topics: ["Retail"],
    status: "published",
  },
  {
    slug: "how-to-choose-a-foot-traffic-data-provider",
    title: "How to choose a foot traffic data provider",
    summary:
      "Are you paying for more platform than you use? And, if you are a consultancy, does your provider fit your business model? How we would think about the choice.",
    published: "2026-09-08",
    publishedDisplay: "September 8, 2026",
    modified: "2026-09-08",
    cardImage:
      "/art/blog/how-to-choose-a-foot-traffic-data-provider/provider-comparison-card.webp",
    socialImage:
      "/art/blog/how-to-choose-a-foot-traffic-data-provider/social-preview.webp",
    heroImage: "/art/blog/how-to-choose-a-foot-traffic-data-provider/hero.webp",
    type: "comparison",
    authors: ["kjartan"],
    status: "published",
  },
  {
    slug: "what-is-a-catchment-area",
    title:
      "What is a catchment area? Definition, methods and how to measure it",
    summary:
      "A catchment area is the geography a location draws its visitors from. How it is defined depends on how it is measured: a radius, a travel-time boundary, or observed mobility.",
    published: "2026-09-08",
    publishedDisplay: "September 8, 2026",
    modified: "2026-09-08",
    cardImage: "/art/blog/what-is-a-catchment-area/catchment-area-card.webp",
    cardImageHotspot: { top: "79%", left: "51%" },
    socialImage: "/art/blog/what-is-a-catchment-area/social-preview.webp",
    bannerImage:
      "/art/blog/what-is-a-catchment-area/catchment-area-banner.webp",
    heroImage: "/art/blog/what-is-a-catchment-area/hero.webp",
    type: "basics",
    authors: ["izabela"],
    status: "published",
  },
  {
    slug: "location-data-vs-location-intelligence",
    title:
      "Location data vs. location intelligence (and aggregated vs. device-level)",
    summary:
      "Four terms that get used interchangeably but mean different things, and the difference matters when you are choosing what location data you actually need.",
    published: "2026-09-08",
    publishedDisplay: "September 8, 2026",
    modified: "2026-09-08",
    cardImage:
      "/art/blog/location-data-vs-location-intelligence/location-data-card.webp",
    socialImage:
      "/art/blog/location-data-vs-location-intelligence/social-preview.webp",
    bannerImage:
      "/art/blog/location-data-vs-location-intelligence/location-data-banner.webp",
    heroImage: "/art/blog/location-data-vs-location-intelligence/hero.webp",
    type: "basics",
    authors: ["kjartan"],
    status: "published",
  },
  {
    slug: "what-is-foot-traffic",
    title: "What is foot traffic data?",
    summary:
      "Foot traffic describes the number of visits to a location over a period. It tells the volume, trend and patterns of activity. No other measure is more central to understanding what happens within places and areas.",
    published: "2026-09-09",
    publishedDisplay: "September 9, 2026",
    modified: "2026-09-09",
    cardImage: "/art/blog/what-is-foot-traffic/foot-traffic-card.webp",
    cardChartLine: FOOT_TRAFFIC_CARD_CHART_LINE,
    socialImage: "/art/blog/what-is-foot-traffic/social-preview.webp",
    heroImage: "/art/blog/what-is-foot-traffic/hero.webp",
    type: "basics",
    authors: ["frode"],
    status: "published",
  },
  {
    slug: "built-on-our-data",
    title: "Some seriously cool things are being built on our data",
    summary:
      "Five ideas from client meetings, from crisis travel guidance to national statistics, and how Places, Areas and Ways could be used to build them. Consider it an invitation.",
    published: "2026-09-10",
    publishedDisplay: "September 10, 2026",
    modified: "2026-09-10",
    cardImage: "/art/blog/built-on-our-data/built-on-our-data-card.webp",
    socialImage: "/art/blog/built-on-our-data/social-preview.webp",
    type: "news",
    authors: ["kjartan"],
    status: "published",
  },
  {
    slug: "future-proof-location-intelligence",
    title:
      "Build for what comes next: future-proof location intelligence with Pine59",
    summary:
      "Privacy regulation keeps moving, and a product built on individual-level location data inherits every change. Why Pine59 is built on aggregated statistics about places and populations, and what that means for the companies building on us.",
    published: "2026-09-23",
    publishedDisplay: "September 23, 2026",
    modified: "2026-09-23",
    cardImage:
      "/art/blog/future-proof-location-intelligence/future-proof-card.webp",
    cardImageHotspots: [
      { top: "67%", left: "21%" },
      { top: "53%", left: "47%" },
      { top: "55%", left: "74%" },
      { top: "70%", left: "93%" },
      { top: "34%", left: "94%" },
    ],
    socialImage:
      "/art/blog/future-proof-location-intelligence/social-preview.webp",
    heroImage: "/art/blog/future-proof-location-intelligence/hero.webp",
    type: "comparison",
    authors: ["kjartan"],
    status: "published",
  },
  {
    slug: "retailnext-integration-live",
    title:
      "Pine59 and RetailNext connect what happens inside and outside the store",
    summary:
      "Pine59 mobile intelligence is now built into the RetailNext platform, giving retailers a connected view of in-store performance and the market beyond their four walls. Available now in the US, with UK coverage planned for early 2027.",
    published: "2026-09-30",
    publishedDisplay: "September 30, 2026",
    modified: "2026-09-30",
    cardImage: "/art/blog/retailnext-integration-live-card.webp",
    socialImage: "/art/blog/social-preview-retailnext-integration-live.webp",
    heroImage: "/art/blog/retailnext-integration-live/hero.webp",
    type: "news",
    authors: ["thomas"],
    topics: ["Retail"],
    status: "published",
  },
  {
    slug: "10-years-in-location-intelligence",
    title: "What we learned from 10+ years in location intelligence",
    summary:
      "Five things we learned building Unacast and now Pine59: why customers want answers, not data, why privacy architecture beats privacy language, and why more data isn't always better data.",
    published: "2026-09-30",
    publishedDisplay: "September 30, 2026",
    modified: "2026-09-30",
    cardImage: "/art/blog/10-years-in-location-intelligence-card.webp",
    socialImage:
      "/art/blog/social-preview-10-years-in-location-intelligence.webp",
    heroImage: "/art/blog/10-years-in-location-intelligence/hero.webp",
    type: "news",
    authors: ["kjartan"],
    status: "published",
  },
  {
    slug: "road-trip-brands",
    title: "How far do people drive for a brand?",
    summary:
      "At the average Buc-ee's, one in three visitors comes from 50 miles or more away. At the average Costco, one in thirteen. A ranking of America's destination brands from aggregated home trade areas, and why Dollar General looks like one when it is not.",
    dek: "A ranking of the country’s destination brands, built from where each store’s visitors come from. Buc-ee’s tops it. Costco is nowhere near. And Dollar General is in there for a reason worth understanding.",
    published: "2026-10-16",
    publishedDisplay: "October 16, 2026",
    modified: "2026-10-16",
    cardImage: "/art/blog/road-trip-brands/road-trip-brands-card.webp",
    socialImage: "/art/blog/road-trip-brands/social-preview.webp",
    heroImage: "/art/blog/road-trip-brands/road-trip-brands-card.webp",
    type: "research",
    authors: ["vemund"],
    topics: ["Retail"],
    status: "published",
  },
  {
    slug: "you-probably-dont-need-device-level-location-data",
    title: "You probably don't need device-level location data",
    summary:
      "Most location questions are about places and populations, not individual devices. Why start from device-level data when the answer you want is a statistic?",
    published: "2026-10-16",
    publishedDisplay: "October 16, 2026",
    modified: "2026-10-16",
    // Figma "Landing Page - Internal Assets": cover 898:3666, social 900:4428.
    cardImage:
      "/art/blog/you-probably-dont-need-device-level-location-data/device-level-data-card.webp",
    socialImage:
      "/art/blog/you-probably-dont-need-device-level-location-data/social-preview.webp",
    heroImage:
      "/art/blog/you-probably-dont-need-device-level-location-data/device-level-data-card.webp",
    // the phone in the cover (edges x 451-674, y 137-578 of 1170x739),
    // scaled to the 1600x1000 card crop
    cardImageHalo: {
      top: "18.2%",
      left: "38.5%",
      width: "19.1%",
      height: "60.3%",
      radius: "12% / 6%",
    },
    type: "comparison",
    authors: ["kjartan"],
    status: "published",
  },
  {
    slug: "why-choose-pine59",
    title: "Why choose Pine59 as your next location intelligence partner",
    summary:
      "Pine59 answers the same core questions as other location intelligence platforms. What sets it apart: how the counts are made, how stable history stays, what customers may do with the data, and an API built for AI workflows.",
    published: "2026-10-20",
    publishedDisplay: "October 20, 2026",
    modified: "2026-10-20",
    // Figma "Landing Page - Internal Assets": cover 933:4446 (exported at 2x,
    // center-cropped to 16:10), social 933:4448. No heroImage: the cover has
    // the logo and a headline baked in, which would collide with the title.
    cardImage: "/art/blog/why-choose-pine59/why-choose-pine59-card.webp",
    socialImage: "/art/blog/why-choose-pine59/social-preview.webp",
    type: "comparison",
    authors: ["torage"],
    status: "published",
  },
  {
    slug: "privacy-by-design",
    title: "Privacy by design: from location data to location intelligence",
    summary:
      "Privacy is not something we add at the end. How Pine59 protects the people behind the observations, from aggregation at the source to thresholds, noise and rounding in the published statistics.",
    published: "2026-10-23",
    publishedDisplay: "October 23, 2026",
    modified: "2026-10-23",
    type: "basics",
    authors: ["kjartan"],
    status: "published",
  },
]

// Everything the public sees, newest first. Published posts sort by date,
// same-day posts keep registry order; placeholders keep registry order and sit
// after the newest published post. Drafts are left out on purpose, which keeps
// them off "keep reading" and prev/next; the index adds DRAFT_POSTS itself,
// off production only. The comparator must
// be consistent (return 0 on ties), or JS engines may sort differently and SSR
// and client diverge.
export const VISIBLE_POSTS: Post[] = [
  ...POSTS.filter((p) => p.status === "published").sort(
    (a, b) =>
      b.published!.localeCompare(a.published!) ||
      POSTS.indexOf(a) - POSTS.indexOf(b),
  ),
  ...POSTS.filter((p) => p.status === "placeholder"),
]

// Drafts, newest first. Listed on the index only off production, so a PR
// preview shows the draft's card the way it will look once published.
export const DRAFT_POSTS: Post[] = POSTS.filter(
  (p) => p.status === "draft",
).sort((a, b) => (b.published ?? "").localeCompare(a.published ?? ""))

// True on the live site. Everything else (PR previews, localhost) shows
// drafts in the index.
export function isProductionHost(hostname: string): boolean {
  return hostname === "pine59.com" || hostname === "www.pine59.com"
}

export const PUBLISHED_POSTS: Post[] = VISIBLE_POSTS.filter(
  (p) => p.status === "published",
)

// The large cards at the top of the index (max 3 so the grid stays the focus).
export const FEATURED_POSTS: Post[] = VISIBLE_POSTS.filter(
  (p) => p.featured,
).slice(0, 3)

// Everything else, for the dense grid.
export const GRID_POSTS: Post[] = VISIBLE_POSTS.filter((p) => !p.featured)

// The filter tabs on the index, in POST_TYPES order: types flagged `tab` that
// also have at least one visible post, so the filter never shows an empty tab.
export const ACTIVE_POST_TYPES = POST_TYPES.filter(
  (t) => t.tab && VISIBLE_POSTS.some((p) => p.type === t.value),
)

// Distinct subject topics across visible posts, in first-seen order.
export const POST_TOPICS: string[] = [
  ...new Set(VISIBLE_POSTS.flatMap((p) => p.topics ?? [])),
]

export function getPost(slug: string): Post | undefined {
  return POSTS.find((p) => p.slug === slug)
}

// Up to `count` other published posts for the "keep reading" grid at the
// foot of a post — same type scores highest, a shared topic next, ties break
// newest-first. Placeholders are excluded; "read article" shouldn't lead to
// a coming-soon page.
export function relatedPosts(slug: string, count = 3): Post[] {
  const current = getPost(slug)
  const candidates = PUBLISHED_POSTS.filter((p) => p.slug !== slug)
  if (!current) return candidates.slice(0, count)

  const score = (p: Post) => {
    let s = 0
    if (p.type === current.type) s += 2
    if (current.topics?.some((t) => p.topics?.includes(t))) s += 1
    return s
  }

  return [...candidates]
    .sort(
      (a, b) => score(b) - score(a) || b.published!.localeCompare(a.published!),
    )
    .slice(0, count)
}

// Absolute URL of the image to use in og:image / JSON-LD for a post: its
// dedicated `socialImage`, else its `cardImage` (at least relevant, if the
// wrong ratio), else the site-wide card. Shared with the launch post's route,
// which builds its own meta and can't go through postMeta().
export function postSocialImage(post: Post): string {
  const path = post.socialImage ?? post.cardImage
  return path ? `${SITE_URL}${path}` : OG_IMAGE
}

// The <meta> block for a post route. Placeholders and drafts are noindex with
// no article metadata; published posts get og:type=article, the article:*
// times and BlogPosting JSON-LD. Keeps every post route's `meta` export to one line.
export function postMeta(post: Post, pathname: string): MetaDescriptor[] {
  const url = canonicalUrl(pathname)
  const published = post.published ?? post.modified
  const extra: MetaDescriptor[] = []
  const socialImage = postSocialImage(post)

  if (post.status === "published" && published) {
    extra.push(
      { property: "article:published_time", content: published },
      {
        property: "article:modified_time",
        content: post.modified ?? published,
      },
      {
        "script:ld+json": {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.summary,
          datePublished: published,
          dateModified: post.modified ?? published,
          image: socialImage,
          author: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
          publisher: {
            "@type": "Organization",
            name: SITE_NAME,
            url: SITE_URL,
            logo: {
              "@type": "ImageObject",
              url: `${SITE_URL}/brand/logo-full.svg`,
            },
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
        },
      },
    )
  }

  return pageMeta({
    title: `${post.title} · Blog`,
    description: post.summary,
    ogTitle: post.title,
    ogDescription: post.summary,
    ogType: "article",
    robots:
      post.status === "published"
        ? "index, follow, max-image-preview:large"
        : "noindex, nofollow",
    pathname,
    image: socialImage,
    extra,
  })
}
